const { Track } = require('livekit-client');
const { BackgroundBlur, VirtualBackground } = require('@livekit/track-processors');
const { LKBase } = require('./lk-base');
const { LK_EVENTS } = require('./lk-events');
const { UI } = require('./lk-ui');

class LKCamera extends LKBase {
  /**
   * Video conferencing module with participant grid.
   * @param {HTMLElement} container
   * @param {object} options - LKBase options + camera-specific:
   * @param {boolean} [options.startWithCamera=true]
   * @param {boolean} [options.startWithMic=true]
   * @param {string}  [options.layout='grid']      - 'grid' | 'spotlight'
   * @param {number}  [options.maxVisibleTiles=9]
   * @param {boolean} [options.showControls=true]
   */
  constructor(container, options) {
    super(container, options);
    this._startWithCamera = options.startWithCamera !== false;
    this._startWithMic = options.startWithMic !== false;
    this._layout = options.layout || 'grid';
    this._maxTiles = options.maxVisibleTiles || 9;
    this._showControls = options.showControls !== false;
    this._cameraEnabled = this._startWithCamera;
    this._micEnabled = this._startWithMic;
    this._tiles = new Map();      // identity → HTMLElement
    this._gridEl = null;
    this._controlsEl = null;
    this._activeFilter = null;   // 'blur' | 'background' | null
    this._processor = null;      // current track processor
  }

  // ─── Public API ──────────────────────────────────────────────────

  async connect() {
    await super.connect();

    // Enable camera (graceful — works even if no device)
    if (this._startWithCamera) {
      try {
        console.log('[LKCamera] Requesting camera...');
        await this.room.localParticipant.setCameraEnabled(true);
        console.log('[LKCamera] Camera enabled');
      } catch (err) {
        console.warn('[LKCamera] Camera unavailable:', err.message);
        this._cameraEnabled = false;
        this._emit(LK_EVENTS.ERROR, { error: err, context: 'enableCamera' });
      }
    }

    // Enable mic (graceful — works even if no device)
    if (this._startWithMic) {
      try {
        console.log('[LKCamera] Requesting microphone...');
        await this.room.localParticipant.setMicrophoneEnabled(true);
        console.log('[LKCamera] Microphone enabled');
      } catch (err) {
        console.warn('[LKCamera] Microphone unavailable:', err.message);
        this._micEnabled = false;
        this._emit(LK_EVENTS.ERROR, { error: err, context: 'enableMicrophone' });
      }
    }

    // Local tracks are attached via _onLocalTrackPublished event
    this._updateLocalTileStatus();
    if (this._controlsEl) {
      UI.toggleControl(this._controlsEl, 'camera', !this._cameraEnabled);
      UI.toggleControl(this._controlsEl, 'mic', !this._micEnabled);
    }
  }

  async toggleCamera() {
    if (!this.room) return;
    this._cameraEnabled = !this._cameraEnabled;
    await this.room.localParticipant.setCameraEnabled(this._cameraEnabled);
    this._updateLocalTileStatus();
    if (this._controlsEl) {
      UI.toggleControl(this._controlsEl, 'camera', !this._cameraEnabled);
    }
  }

  async toggleMicrophone() {
    if (!this.room) return;
    this._micEnabled = !this._micEnabled;
    await this.room.localParticipant.setMicrophoneEnabled(this._micEnabled);
    this._updateLocalTileStatus();
    if (this._controlsEl) {
      UI.toggleControl(this._controlsEl, 'mic', !this._micEnabled);
    }
  }

  async switchCamera(deviceId) {
    if (!this.room) return;
    await this.room.switchActiveDevice('videoinput', deviceId);
    this._emit(LK_EVENTS.DEVICE_CHANGED, { kind: 'videoinput', deviceId });
  }

  async switchMicrophone(deviceId) {
    if (!this.room) return;
    await this.room.switchActiveDevice('audioinput', deviceId);
    this._emit(LK_EVENTS.DEVICE_CHANGED, { kind: 'audioinput', deviceId });
  }

  setLayout(mode) {
    if (mode !== 'grid' && mode !== 'spotlight') return;
    this._layout = mode;
    if (this._gridEl) {
      this._gridEl.className = 'lk-grid lk-grid--' + mode;
    }
    this._emit(LK_EVENTS.LAYOUT_CHANGED, { mode });
  }

  async getMediaDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      cameras: devices.filter(d => d.kind === 'videoinput'),
      microphones: devices.filter(d => d.kind === 'audioinput'),
      speakers: devices.filter(d => d.kind === 'audiooutput')
    };
  }

  // ─── Filters (background blur / virtual background) ──────────────

  /**
   * Apply background blur.
   * @param {number} [blurRadius=10] - Blur intensity (1-20)
   */
  async setBackgroundBlur(blurRadius = 10) {
    await this._removeProcessor();
    const cameraPub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (!cameraPub || !cameraPub.track) return;

    this._processor = BackgroundBlur(blurRadius);
    await cameraPub.track.setProcessor(this._processor);
    this._activeFilter = 'blur';
    console.log('[LKCamera] Background blur applied, radius:', blurRadius);
  }

  /**
   * Replace background with an image.
   * @param {string} imageSrc - URL or data URI of the background image
   */
  async setVirtualBackground(imageSrc) {
    await this._removeProcessor();
    const cameraPub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (!cameraPub || !cameraPub.track) return;

    this._processor = VirtualBackground(imageSrc);
    await cameraPub.track.setProcessor(this._processor);
    this._activeFilter = 'background';
    console.log('[LKCamera] Virtual background applied');
  }

  /**
   * Remove any active filter.
   */
  async removeFilter() {
    await this._removeProcessor();
    this._activeFilter = null;
    console.log('[LKCamera] Filter removed');
  }

  /** @returns {'blur'|'background'|null} */
  get activeFilter() {
    return this._activeFilter;
  }

  async _removeProcessor() {
    if (!this.room) return;
    const cameraPub = this.room.localParticipant.getTrackPublication(Track.Source.Camera);
    if (cameraPub && cameraPub.track) {
      try {
        await cameraPub.track.stopProcessor();
      } catch (e) {
        // No processor active, ignore
      }
    }
    this._processor = null;
    this._activeFilter = null;
  }

  // ─── UI rendering ────────────────────────────────────────────────

  _renderUI() {
    if (!this.container) return;
    this.container.classList.add('lk-container', 'lk-camera-mode');

    // Grid
    this._gridEl = document.createElement('div');
    this._gridEl.className = 'lk-grid lk-grid--' + this._layout;
    this.container.appendChild(this._gridEl);

    // Local tile
    this._addTile(this.room.localParticipant, true);

    // Existing remote participants
    for (const [, p] of this.room.remoteParticipants) {
      this._addTile(p, false);
    }

    this._updateGridLayout();

    // Controls
    if (this._showControls) {
      this._controlsEl = UI.createControls([
        {
          id: 'mic',
          icon: UI.icons.mic,
          label: 'Toggle microphone',
          onClick: () => this.toggleMicrophone()
        },
        {
          id: 'camera',
          icon: UI.icons.camera,
          label: 'Toggle camera',
          onClick: () => this.toggleCamera()
        },
        {
          id: 'layout',
          icon: UI.icons.layout,
          label: 'Switch layout',
          onClick: () => this.setLayout(this._layout === 'grid' ? 'spotlight' : 'grid')
        },
        {
          id: 'hangup',
          icon: UI.icons.hangup,
          label: 'Disconnect',
          className: 'lk-ctrl-btn--danger',
          onClick: () => this.disconnect()
        }
      ]);
      this.container.appendChild(this._controlsEl);

      if (!this._micEnabled) UI.toggleControl(this._controlsEl, 'mic', true);
      if (!this._cameraEnabled) UI.toggleControl(this._controlsEl, 'camera', true);
    }
  }

  _cleanupUI() {
    this._tiles.clear();
    this._gridEl = null;
    this._controlsEl = null;

    if (this.container) {
      this.container.classList.remove('lk-container', 'lk-camera-mode');
      this.container.innerHTML = '';
    }
  }

  _addTile(participant, isLocal) {
    if (!this._gridEl) return;
    const tile = UI.createTile(participant, { isLocal });
    this._gridEl.appendChild(tile);
    this._tiles.set(participant.identity, tile);

    // Attach existing video/audio tracks
    for (const [, pub] of participant.trackPublications) {
      if (pub.track && pub.isSubscribed) {
        if (pub.track.kind === Track.Kind.Video) {
          UI.attachTrackToTile(tile, pub.track);
        } else if (pub.track.kind === Track.Kind.Audio && !isLocal) {
          UI.attachAudioTrack(pub.track);
        }
      }
    }

    this._updateTileStatus(tile, participant);
    this._updateGridLayout();
  }

  _removeTile(participant) {
    const tile = this._tiles.get(participant.identity);
    if (tile) {
      tile.remove();
      this._tiles.delete(participant.identity);
    }
    this._updateGridLayout();
  }

  _updateLocalTileStatus() {
    const tile = this._tiles.get(this.room.localParticipant.identity);
    if (tile) this._updateTileStatus(tile, this.room.localParticipant);
  }

  _updateTileStatus(tile, participant) {
    const micPub = participant.getTrackPublication(Track.Source.Microphone);
    const camPub = participant.getTrackPublication(Track.Source.Camera);
    UI.updateTileStatus(tile, {
      micMuted: !micPub || micPub.isMuted,
      cameraMuted: !camPub || camPub.isMuted
    });
  }

  _updateGridLayout() {
    if (!this._gridEl) return;
    const count = this._tiles.size;
    // CSS grid columns based on participant count
    let cols = 1;
    if (count >= 2) cols = 2;
    if (count >= 5) cols = 3;
    if (count >= 10) cols = 4;
    this._gridEl.style.setProperty('--lk-grid-cols', cols);
  }

  // ─── Room event hooks ────────────────────────────────────────────

  _onLocalTrackPublished(publication, participant) {
    const tile = this._tiles.get(this.room.localParticipant.identity);
    if (!tile) return;
    console.log('[LKCamera] Local track published:', publication.kind, publication.source);
    if (publication.track && publication.track.kind === Track.Kind.Video) {
      UI.attachTrackToTile(tile, publication.track);
    }
    this._updateTileStatus(tile, this.room.localParticipant);
  }

  _onParticipantConnected(participant) {
    this._addTile(participant, false);
  }

  _onParticipantDisconnected(participant) {
    this._removeTile(participant);
  }

  _onTrackSubscribed(track, publication, participant) {
    const tile = this._tiles.get(participant.identity);
    console.log('[LKCamera] TrackSubscribed:', track.kind, track.source, 'from', participant.identity, 'tile:', !!tile);

    if (!tile) return;

    if (track.kind === Track.Kind.Video) {
      UI.attachTrackToTile(tile, track);
    } else if (track.kind === Track.Kind.Audio) {
      const el = UI.attachAudioTrack(track);
      console.log('[LKCamera] Audio element attached, paused:', el.paused, 'muted:', el.muted, 'srcObject:', !!el.srcObject);
    }
    this._updateTileStatus(tile, participant);
  }

  _onTrackUnsubscribed(track, publication, participant) {
    const tile = this._tiles.get(participant.identity);
    if (track.kind === Track.Kind.Video && tile) {
      UI.detachTrackFromTile(tile, track);
    } else if (track.kind === Track.Kind.Audio) {
      UI.detachAudioTrack(track);
    }
  }

  _onActiveSpeakersChanged(speakers) {
    // Reset all
    for (const [, tile] of this._tiles) {
      UI.setTileSpeaking(tile, false);
    }
    for (const s of speakers) {
      const tile = this._tiles.get(s.identity);
      UI.setTileSpeaking(tile, true);
    }
  }
}

module.exports = { LKCamera };
